/* eslint-disable @typescript-eslint/consistent-type-definitions -- Zero mutator/query arg types must be `type` aliases to satisfy the ReadonlyJSONValue constraint; interfaces lack an implicit index signature. */
import { defineMutator } from "@rocicorp/zero"

import type { ZeroJwtPayload } from "@productlane/lib/src/browser"
import type {
	ExistingOpenProposalForCheck,
	ProposalCreationActor,
} from "@productlane/lib/src/prosemirror-diff"
import {
	evaluateProposalCreation,
	toExistingOpenProposal,
} from "@productlane/lib/src/prosemirror-diff"

import type { MutatorTx } from "../schema"
import type { JSONValue } from "@rocicorp/zero"
import {
	applyCreateProposal,
	applyDeleteProposal,
	applyEditProposal,
} from "./help-center-proposal-apply"
import {
	closeParentGroupIfAllTerminal,
	closeProposalAs,
} from "./help-center-proposal-status"

// Thrown by the create / promote-to-open paths when a new EDIT proposal would
// fully conflict with already-open proposals on the article. The
// propose-edit-screen catches this by name and surfaces `reason` in a toast.
export class ProposalConflictError extends Error {
	readonly reason: string
	readonly conflictingProposalIds: string[]
	constructor(reason: string, conflictingProposalIds: string[]) {
		super(reason)
		this.name = "ProposalConflictError"
		this.reason = reason
		this.conflictingProposalIds = conflictingProposalIds
	}
}

export type CreateProposalArgs = {
	id: string
	kind?: "EDIT" | "CREATE" | "DELETE"
	articleId?: string
	// FK to the HelpCenterArticleVersion this proposal was drafted against.
	// Required for EDIT proposals (the version's content is the base for
	// `proposedSteps`); omit for CREATE (no article exists yet) and DELETE
	// (no diff). The DB column is nullable but the EDIT path will be a no-op
	// without it.
	baseVersionId?: string
	proposalGroupId?: string
	// Serialized ProseMirror Step[] (Step.toJSON() output) describing the
	// proposed change against the base version's content. Empty array for
	// DELETE.
	proposedSteps: JSONValue
	proposedTitle?: string
	proposedGroupId?: string
	source?: "MANUAL" | "CHANGELOG" | "ISSUE_COMPLETED"
	authorType?: "USER" | "AGENT"
	// When true, the proposal is immediately workspace-visible; otherwise DRAFT.
	submitForReview?: boolean
}

export type UpdateDraftProposalArgs = {
	id: string
	proposedSteps?: JSONValue
	baseVersionId?: string | null
	proposedTitle?: string | null
	proposedGroupId?: string | null
}

export type UpdateProposalTranslationArgs = {
	proposalId: string
	language: string
	title?: string | null
	content?: string
}

export type AcceptProposalArgs = {
	id: string
	// Client-generated IDs for any rows the accept path will create.
	newArticleId?: string
	newArticleVersionId: string
	newArticleUrlName?: string
	// Hunk ids (`${proposalId}#${index}`) the reviewer discarded. The EDIT apply
	// path re-synthesizes the proposal's steps from its base keeping only the
	// non-discarded hunks before applying.
	discardedHunkIds?: string[]
	editedHtml?: string
	editedTitle?: string
}

export type RejectProposalArgs = {
	id: string
	// Why the reviewer declined, so the docs agent can learn from it.
	reason?: string
	note?: string
}

export type UnacceptProposalArgs = {
	id: string
	// Same IDs the client passed to `accept` — used to locate the rows the
	// accept side effects produced.
	newArticleVersionId: string
	newArticleId?: string
}

export type ReopenProposalArgs = {
	id: string
}

// Resolve the article's current head HTML and the list of currently-OPEN
// proposals (with their `baseHtml` resolved from each proposal's
// `baseVersionId` and their provenance issueIds resolved via the parent
// group's join rows) — the inputs `evaluateProposalCreation` needs to make a
// decision. `excludeProposalId` is used by the promote-to-open path so a
// proposal doesn't see itself in the existing list when its own status is
// flipping from DRAFT to OPEN.
async function loadProposalCheckInputs(
	tx: MutatorTx,
	articleId: string,
	excludeProposalId?: string,
): Promise<{
	currentHtml: string
	existingOpenProposals: ExistingOpenProposalForCheck[]
}> {
	const versions = await tx.query.helpCenterArticleVersions
		.where("articleId", "=", articleId)
		.where("language", "=", "en")
		.run()
	const sortedVersions = [...versions].sort((a, b) => a.createdAt - b.createdAt)
	const head = sortedVersions[sortedVersions.length - 1]
	const currentHtml =
		head && typeof head.content === "string" ? head.content : ""

	const proposals = await tx.query.helpCenterArticleProposals
		.where("articleId", "=", articleId)
		.where("status", "=", "OPEN")
		.run()
	const filtered = excludeProposalId
		? proposals.filter((p) => p.id !== excludeProposalId)
		: proposals

	const versionById = new Map(sortedVersions.map((v) => [v.id, v]))

	const groupIds = Array.from(
		new Set(
			filtered
				.map((p) => p.proposalGroupId)
				.filter((id): id is string => typeof id === "string"),
		),
	)
	const groupIssueIds = await loadIssueIdsByGroup(tx, groupIds)

	const existingOpenProposals: ExistingOpenProposalForCheck[] = filtered.map(
		(p) => {
			const baseVersion = p.baseVersionId
				? versionById.get(p.baseVersionId)
				: undefined
			const baseHtml =
				baseVersion && typeof baseVersion.content === "string"
					? baseVersion.content
					: ""
			const issueIds = p.proposalGroupId
				? (groupIssueIds.get(p.proposalGroupId) ?? [])
				: []
			return toExistingOpenProposal(p, baseHtml, issueIds)
		},
	)

	return { currentHtml, existingOpenProposals }
}

// Bulk-fetch the join rows for a set of proposal-group ids, grouped by group
// id so callers can look up each proposal's provenance issueIds in O(1).
async function loadIssueIdsByGroup(
	tx: MutatorTx,
	groupIds: string[],
): Promise<Map<string, string[]>> {
	if (groupIds.length === 0) return new Map()
	const links = await tx.query.helpCenterProposalGroupToIssues
		.where("A", "IN", groupIds)
		.run()
	const out = new Map<string, string[]>()
	for (const link of links) {
		const list = out.get(link.A)
		if (list) list.push(link.B)
		else out.set(link.A, [link.B])
	}
	return out
}

// Resolve the proposal's base HTML — usually the article's head version
// content at draft time. Falls back to current head when the version row is
// missing (legacy / pruned).
async function resolveBaseHtmlForArgs(
	tx: MutatorTx,
	articleId: string,
	baseVersionId: string | null,
): Promise<string> {
	if (baseVersionId) {
		const [version] = await tx.query.helpCenterArticleVersions
			.where("id", "=", baseVersionId)
			.run()
		if (version && typeof version.content === "string") return version.content
	}
	const versions = await tx.query.helpCenterArticleVersions
		.where("articleId", "=", articleId)
		.where("language", "=", "en")
		.run()
	const sorted = [...versions].sort((a, b) => a.createdAt - b.createdAt)
	const head = sorted[sorted.length - 1]
	return head && typeof head.content === "string" ? head.content : ""
}

async function actorFromCreateArgs(
	tx: MutatorTx,
	args: CreateProposalArgs,
	authData: ZeroJwtPayload,
): Promise<ProposalCreationActor> {
	if (args.authorType === "AGENT") {
		const issueIds = args.proposalGroupId
			? ((await loadIssueIdsByGroup(tx, [args.proposalGroupId])).get(
					args.proposalGroupId,
				) ?? [])
			: []
		return { type: "AGENT", issueIds }
	}
	return { type: "USER", userId: authData.userId }
}

// Apply the new content from a (just-arrived) creation request onto an
// existing OPEN proposal — the same-actor coalesce path. Status stays OPEN
// because the existing row was already submitted for review; we're just
// refining its contents.
async function coalesceIntoExistingProposal(
	tx: MutatorTx,
	targetId: string,
	args: CreateProposalArgs,
	now: number,
) {
	const [existing] = await tx.query.helpCenterArticleProposals
		.where("id", "=", targetId)
		.run()
	if (!existing) return
	await tx.mutate.helpCenterArticleProposals.update({
		id: targetId,
		proposedSteps: args.proposedSteps,
		baseVersionId: args.baseVersionId ?? existing.baseVersionId,
		proposedTitle:
			args.proposedTitle !== undefined
				? args.proposedTitle
				: existing.proposedTitle,
		updatedAt: now,
		version: existing.version + 1,
	})
}

// Symmetric to `closeParentGroupIfAllTerminal`: when a child proposal flips
// back to OPEN (via `unaccept` / `reopen`), the parent group can no longer be
// terminal. Flip it back to OPEN and clear the close audit fields.
async function reopenParentGroupIfClosed(
	tx: MutatorTx,
	groupId: string,
	now: number,
) {
	const [group] = await tx.query.helpCenterProposalGroups
		.where("id", "=", groupId)
		.run()
	if (!group || group.status === "OPEN") return

	await tx.mutate.helpCenterProposalGroups.update({
		id: groupId,
		status: "OPEN",
		closedByUserId: undefined,
		closedAt: undefined,
		updatedAt: now,
	})
}

export const helpCenterArticleProposalMutators = {
	create: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: CreateProposalArgs
			ctx: ZeroJwtPayload
		}) => {
			const now = Date.now()
			const kind = args.kind ?? "EDIT"
			const status = args.submitForReview ? "OPEN" : "DRAFT"

			// Conflict-check + same-actor coalesce only apply to EDIT proposals
			// that go straight to OPEN. CREATE has no article to lock against,
			// DELETE has no diff, and DRAFTs are private to their author.
			const shouldEvaluate =
				kind === "EDIT" && status === "OPEN" && !!args.articleId

			if (shouldEvaluate && args.articleId) {
				const { currentHtml, existingOpenProposals } =
					await loadProposalCheckInputs(tx, args.articleId)
				const newBaseHtml = await resolveBaseHtmlForArgs(
					tx,
					args.articleId,
					args.baseVersionId ?? null,
				)
				const decision = evaluateProposalCreation({
					newProposalId: args.id,
					newBaseHtml,
					newProposedSteps: Array.isArray(args.proposedSteps)
						? (args.proposedSteps as unknown[])
						: [],
					currentHtml,
					existingOpenProposals,
					actor: await actorFromCreateArgs(tx, args, authData),
				})

				if (decision.action === "reject") {
					throw new ProposalConflictError(
						decision.reason,
						decision.conflictingProposalIds,
					)
				}
				if (decision.action === "updateExisting") {
					await coalesceIntoExistingProposal(
						tx,
						decision.targetProposalId,
						args,
						now,
					)
					return
				}
				// fallthrough: action === "create"
			}

			await tx.mutate.helpCenterArticleProposals.insert({
				id: args.id,
				createdAt: now,
				updatedAt: now,
				version: 0,
				kind,
				proposalGroupId: args.proposalGroupId,
				articleId: args.articleId,
				baseVersionId: args.baseVersionId,
				proposedSteps: args.proposedSteps,
				proposedTitle: args.proposedTitle,
				proposedGroupId: args.proposedGroupId,
				source: args.source ?? "MANUAL",
				authorType: args.authorType ?? "USER",
				createdByUserId: authData.userId,
				status,
				workspaceId: authData.workspaceId,
			})
		},
	),

	updateDraft: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: UpdateDraftProposalArgs }) => {
			const [proposal] = await tx.query.helpCenterArticleProposals
				.where("id", "=", args.id)
				.run()

			if (!proposal || proposal.status !== "DRAFT") {
				// Don't throw — log and exit so the poke handler doesn't crash.
				console.warn(
					`updateDraft: proposal ${args.id} not found or not DRAFT, skipping`,
				)
				return
			}

			await tx.mutate.helpCenterArticleProposals.update({
				id: args.id,
				proposedSteps: args.proposedSteps ?? proposal.proposedSteps,
				baseVersionId:
					args.baseVersionId !== undefined
						? (args.baseVersionId ?? undefined)
						: proposal.baseVersionId,
				proposedTitle:
					args.proposedTitle !== undefined
						? (args.proposedTitle ?? undefined)
						: proposal.proposedTitle,
				proposedGroupId:
					args.proposedGroupId !== undefined
						? (args.proposedGroupId ?? undefined)
						: proposal.proposedGroupId,
				updatedAt: Date.now(),
			})
		},
	),

	updateTranslation: defineMutator(
		async ({
			tx,
			args,
		}: {
			tx: MutatorTx
			args: UpdateProposalTranslationArgs
		}) => {
			const [draft] = await tx.query.helpCenterArticleProposalTranslations
				.where("proposalId", "=", args.proposalId)
				.where("language", "=", args.language)
				.run()
			if (!draft) {
				console.warn(
					`updateTranslation: no ${args.language} draft for proposal ${args.proposalId}, skipping`,
				)
				return
			}
			await tx.mutate.helpCenterArticleProposalTranslations.update({
				id: draft.id,
				title:
					args.title !== undefined ? (args.title ?? undefined) : draft.title,
				content: args.content ?? draft.content,
				version: draft.version + 1,
				updatedAt: Date.now(),
			})
		},
	),

	promoteToOpen: defineMutator(
		async ({ tx, args: { id } }: { tx: MutatorTx; args: { id: string } }) => {
			const [proposal] = await tx.query.helpCenterArticleProposals
				.where("id", "=", id)
				.run()

			if (!proposal || proposal.status !== "DRAFT") {
				console.warn(
					`promoteToOpen: proposal ${id} not found or not DRAFT, skipping`,
				)
				return
			}

			const now = Date.now()

			// Apply the same conflict-check + same-actor coalesce as `create`
			// when an EDIT proposal goes OPEN. Drafts are private, but as soon
			// as one becomes visible to the workspace it has to play by the
			// file-locking rules.
			if (proposal.kind === "EDIT" && proposal.articleId) {
				const { currentHtml, existingOpenProposals } =
					await loadProposalCheckInputs(tx, proposal.articleId, proposal.id)
				const baseHtml = await resolveBaseHtmlForArgs(
					tx,
					proposal.articleId,
					proposal.baseVersionId ?? null,
				)
				const groupIssueIds = proposal.proposalGroupId
					? ((await loadIssueIdsByGroup(tx, [proposal.proposalGroupId])).get(
							proposal.proposalGroupId,
						) ?? [])
					: []
				const actor: ProposalCreationActor =
					proposal.authorType === "AGENT"
						? { type: "AGENT", issueIds: groupIssueIds }
						: { type: "USER", userId: proposal.createdByUserId ?? null }
				const decision = evaluateProposalCreation({
					newProposalId: proposal.id,
					newBaseHtml: baseHtml,
					newProposedSteps: Array.isArray(proposal.proposedSteps)
						? (proposal.proposedSteps as unknown[])
						: [],
					currentHtml,
					existingOpenProposals,
					actor,
				})

				if (decision.action === "reject") {
					throw new ProposalConflictError(
						decision.reason,
						decision.conflictingProposalIds,
					)
				}
				if (decision.action === "updateExisting") {
					// Copy this draft's contents onto the existing OPEN proposal,
					// then drop the draft. The draft was a private workspace for
					// the same actor — its content is what matters now, not the
					// row itself.
					const [existing] = await tx.query.helpCenterArticleProposals
						.where("id", "=", decision.targetProposalId)
						.run()
					if (existing) {
						await tx.mutate.helpCenterArticleProposals.update({
							id: existing.id,
							proposedSteps: proposal.proposedSteps,
							baseVersionId: proposal.baseVersionId ?? existing.baseVersionId,
							proposedTitle: proposal.proposedTitle ?? existing.proposedTitle,
							updatedAt: now,
							version: existing.version + 1,
						})
					}
					await tx.mutate.helpCenterArticleProposals.delete({ id })
					return
				}
				// fallthrough: action === "create" — proceed with the simple
				// status flip below.
			}

			await tx.mutate.helpCenterArticleProposals.update({
				id,
				status: "OPEN",
				updatedAt: now,
			})
		},
	),

	accept: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: AcceptProposalArgs
			ctx: ZeroJwtPayload
		}) => {
			const now = Date.now()

			const [proposal] = await tx.query.helpCenterArticleProposals
				.where("id", "=", args.id)
				.run()

			if (
				!proposal ||
				(proposal.status !== "OPEN" && proposal.status !== "DRAFT")
			) {
				console.warn(
					`accept: proposal ${args.id} not found or not acceptable, skipping`,
				)
				return
			}

			const parentGroupId = proposal.proposalGroupId ?? null
			const applyCtx = { workspaceId: authData.workspaceId, now }

			// Dispatch by kind. Each helper returns `{ accepted }` or
			// `{ superseded }` — we map that to the proposal-row update here,
			// since `accept` records `appliedVersionId` for EDIT but not for
			// the others.
			let outcome: { kind: "accepted" } | { kind: "superseded" }
			let appliedVersionId: string | undefined

			if (proposal.kind === "DELETE") {
				outcome = await applyDeleteProposal(tx, proposal, applyCtx)
			} else if (!proposal.articleId) {
				if (!args.newArticleId || !args.newArticleUrlName) {
					console.warn(
						`accept: new-article proposal ${args.id} missing newArticleId/newArticleUrlName, skipping`,
					)
					return
				}
				outcome = await applyCreateProposal(
					tx,
					proposal,
					{
						newArticleId: args.newArticleId,
						newArticleUrlName: args.newArticleUrlName,
						editedHtml: args.editedHtml,
						editedTitle: args.editedTitle,
					},
					applyCtx,
				)
			} else {
				outcome = await applyEditProposal(
					tx,
					proposal,
					{
						newArticleVersionId: args.newArticleVersionId,
						discardedHunkIds: args.discardedHunkIds,
						editedHtml: args.editedHtml,
						editedTitle: args.editedTitle,
					},
					applyCtx,
				)
				if (outcome.kind === "accepted") {
					appliedVersionId = args.newArticleVersionId
				}
			}

			await tx.mutate.helpCenterArticleProposals.update({
				id: args.id,
				...closeProposalAs({
					status: outcome.kind === "accepted" ? "ACCEPTED" : "SUPERSEDED",
					userId: authData.userId,
					now,
					appliedVersionId:
						outcome.kind === "accepted" ? appliedVersionId : undefined,
				}),
			})

			if (parentGroupId) {
				await closeParentGroupIfAllTerminal(
					tx,
					parentGroupId,
					authData.userId,
					now,
				)
			}
		},
	),

	reject: defineMutator(
		async ({
			tx,
			args,
			ctx: authData,
		}: {
			tx: MutatorTx
			args: RejectProposalArgs
			ctx: ZeroJwtPayload
		}) => {
			const [proposal] = await tx.query.helpCenterArticleProposals
				.where("id", "=", args.id)
				.run()

			if (
				!proposal ||
				(proposal.status !== "OPEN" && proposal.status !== "DRAFT")
			) {
				console.warn(
					`reject: proposal ${args.id} not found or not open, skipping`,
				)
				return
			}

			const now = Date.now()
			const parentGroupId = proposal.proposalGroupId ?? null
			await tx.mutate.helpCenterArticleProposals.update({
				id: args.id,
				...closeProposalAs({
					status: "REJECTED",
					userId: authData.userId,
					now,
				}),
			})
			if (parentGroupId) {
				await closeParentGroupIfAllTerminal(
					tx,
					parentGroupId,
					authData.userId,
					now,
				)
			}
		},
	),

	// Inverse of `accept`. Powers the 10s undo affordance on the proposal
	// review card. Best-effort: bails with a console.warn if the proposal
	// is no longer in ACCEPTED state (someone else accepted/edited it
	// downstream while the toast was visible).
	unaccept: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: UnacceptProposalArgs }) => {
			const [proposal] = await tx.query.helpCenterArticleProposals
				.where("id", "=", args.id)
				.run()

			if (!proposal || proposal.status !== "ACCEPTED") {
				console.warn(
					`unaccept: proposal ${args.id} not in ACCEPTED state, skipping`,
				)
				return
			}

			const now = Date.now()
			const parentGroupId = proposal.proposalGroupId ?? null

			if (proposal.kind === "DELETE") {
				if (proposal.articleId) {
					await tx.mutate.helpCenterArticles.update({
						id: proposal.articleId,
						isDeleted: false,
						updatedAt: now,
					})
				}
			} else if (proposal.kind === "CREATE") {
				if (args.newArticleId) {
					await tx.mutate.helpCenterArticles.delete({ id: args.newArticleId })
				}
			} else {
				// EDIT: undo by deleting the version row that accept inserted and
				// restoring the article + translation to the prior head's content.
				// `pruneOldVersions` may have dropped a non-head version row during
				// accept; we don't restore that — the chain just contracts by one.
				const [newVersion] = await tx.query.helpCenterArticleVersions
					.where("id", "=", args.newArticleVersionId)
					.run()
				const parentVersionId = newVersion?.parentVersionId ?? null
				const [parentVersion] = parentVersionId
					? await tx.query.helpCenterArticleVersions
							.where("id", "=", parentVersionId)
							.run()
					: []

				if (proposal.articleId && parentVersion) {
					const parentHtml =
						typeof parentVersion.content === "string"
							? parentVersion.content
							: ""
					const [article] = await tx.query.helpCenterArticles
						.where("id", "=", proposal.articleId)
						.run()
					if (article) {
						await tx.mutate.helpCenterArticles.update({
							id: article.id,
							content: parentHtml,
							title: parentVersion.title,
							version: Math.max(article.version - 1, 1),
							updatedAt: now,
						})
						const [translation] = await tx.query.helpCenterArticleTranslations
							.where("helpCenterArticleId", "=", article.id)
							.where("language", "=", "en")
							.run()
						if (translation) {
							await tx.mutate.helpCenterArticleTranslations.update({
								id: translation.id,
								title: parentVersion.title,
								content: parentHtml,
							})
						}
					}
				}

				if (newVersion) {
					await tx.mutate.helpCenterArticleVersions.delete({
						id: args.newArticleVersionId,
					})
				}
			}

			await tx.mutate.helpCenterArticleProposals.update({
				id: args.id,
				status: "OPEN",
				closedByUserId: undefined,
				closedAt: undefined,
				appliedVersionId: undefined,
				updatedAt: now,
			})

			if (parentGroupId) {
				await reopenParentGroupIfClosed(tx, parentGroupId, now)
			}
		},
	),

	// Inverse of `reject`. Powers the 10s undo affordance on the proposal
	// review card.
	reopen: defineMutator(
		async ({ tx, args }: { tx: MutatorTx; args: ReopenProposalArgs }) => {
			const [proposal] = await tx.query.helpCenterArticleProposals
				.where("id", "=", args.id)
				.run()

			if (!proposal || proposal.status !== "REJECTED") {
				console.warn(
					`reopen: proposal ${args.id} not in REJECTED state, skipping`,
				)
				return
			}

			const now = Date.now()
			const parentGroupId = proposal.proposalGroupId ?? null

			await tx.mutate.helpCenterArticleProposals.update({
				id: args.id,
				status: "OPEN",
				closedByUserId: undefined,
				closedAt: undefined,
				updatedAt: now,
			})

			if (parentGroupId) {
				await reopenParentGroupIfClosed(tx, parentGroupId, now)
			}
		},
	),
}
